import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Mic, MicOff, Send, Sparkles, Loader2, X, MessageCircle, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '@/components/ui/dialog';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BEING_TYPES = ['identity', 'value', 'habit'];

function getSpeechRecognition() {
  if (typeof window === 'undefined') return null;
  return window.SpeechRecognition || window.webkitSpeechRecognition || null;
}

const PLACEHOLDER_KEYS = [
  'aiChat.placeholder.examples.0',
  'aiChat.placeholder.examples.1',
  'aiChat.placeholder.examples.2',
  'aiChat.placeholder.examples.3'
];

export default function AIChat({ token, user, onCommitted, pendingPrompt, onPendingConsumed }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [panelOpen, setPanelOpen] = useState(false);
  const [draft, setDraft] = useState('');
  const [extracting, setExtracting] = useState(false);
  const [proposal, setProposal] = useState(null);
  const [selected, setSelected] = useState({ beings: [], wants: [], todos: [], reflections: [] });
  const [committing, setCommitting] = useState(false);
  const [listening, setListening] = useState(false);
  const [placeholderIdx, setPlaceholderIdx] = useState(0);
  // Conversation state for multi-turn extraction. Each entry: { role: "user" | "assistant", content }.
  // The assistant turns are the AI's clarifying questions; user turns are the original message + replies.
  const [conversation, setConversation] = useState([]);
  const recognitionRef = useRef(null);
  const textareaRef = useRef(null);

  // External thread seed (e.g., Move 2 weekly review). When pendingPrompt arrives,
  // open the panel + start a new thread with that as the assistant's opening turn,
  // then signal back so the parent can clear the prop.
  useEffect(() => {
    if (pendingPrompt) {
      setConversation([{ role: 'assistant', content: pendingPrompt }]);
      setDraft('');
      setProposal(null);
      setPanelOpen(true);
      setTimeout(() => textareaRef.current?.focus(), 150);
      onPendingConsumed?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingPrompt]);

  // Focus the textarea when the user opens the panel manually too.
  useEffect(() => {
    if (panelOpen) {
      setTimeout(() => textareaRef.current?.focus(), 150);
    }
  }, [panelOpen]);

  const SpeechRecognitionCtor = getSpeechRecognition();
  const voiceSupported = !!SpeechRecognitionCtor;

  // Most recent assistant question to display above the input.
  const pendingQuestion = (() => {
    for (let i = conversation.length - 1; i >= 0; i -= 1) {
      if (conversation[i].role === 'assistant') return conversation[i].content;
    }
    return null;
  })();
  const inThread = conversation.length > 0;

  useEffect(() => () => recognitionRef.current?.abort?.(), []);

  // Rotate placeholder examples every 4.5s, but only when idle and no thread in progress.
  useEffect(() => {
    if (draft || extracting || listening || inThread) return;
    const id = setInterval(() => setPlaceholderIdx((i) => (i + 1) % PLACEHOLDER_KEYS.length), 4500);
    return () => clearInterval(id);
  }, [draft, extracting, listening, inThread]);

  const startListening = () => {
    if (!SpeechRecognitionCtor) {
      toast.error(t('aiChat.voice.unsupported'));
      return;
    }
    const recognition = new SpeechRecognitionCtor();
    recognition.lang = navigator.language || 'en-US';
    recognition.interimResults = true;
    recognition.continuous = true;
    let finalText = '';
    recognition.onresult = (event) => {
      let interim = '';
      for (let i = event.resultIndex; i < event.results.length; i += 1) {
        const transcript = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          finalText += transcript;
        } else {
          interim += transcript;
        }
      }
      setDraft(((finalText + ' ' + interim).trim()));
    };
    recognition.onerror = (e) => {
      setListening(false);
      if (e.error !== 'no-speech' && e.error !== 'aborted') {
        toast.error(t('aiChat.voice.error'));
      }
    };
    recognition.onend = () => setListening(false);
    recognitionRef.current = recognition;
    recognition.start();
    setListening(true);
  };

  const stopListening = () => {
    recognitionRef.current?.stop?.();
    setListening(false);
  };

  const resetThread = () => {
    setConversation([]);
    setDraft('');
    setProposal(null);
  };

  const handleExtract = async () => {
    const text = draft.trim();
    if (!text) return;
    if (listening) stopListening();

    const nextConversation = [...conversation, { role: 'user', content: text }];
    setExtracting(true);
    try {
      const { data } = await axios.post(
        `${API}/ai/extract`,
        { messages: nextConversation },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const hasItems =
        data.beings.length + data.wants.length + data.todos.length + data.reflections.length > 0;
      const hasQuestion = !!data.clarifying_question;

      if (hasQuestion && !hasItems) {
        // Pure clarifying turn — extend the conversation, keep the thread alive.
        setConversation([...nextConversation, { role: 'assistant', content: data.clarifying_question }]);
        setDraft('');
        return;
      }

      if (!hasItems && !hasQuestion) {
        toast.info(t('aiChat.empty'));
        return;
      }

      // We have items. If there's also a clarifying question, surface it after commit
      // (we don't have a clean way to do both in one dialog; commit-then-continue is the UX).
      setProposal(data);
      setSelected({
        beings: data.beings.map(() => true),
        wants: data.wants.map(() => true),
        todos: data.todos.map(() => true),
        reflections: data.reflections.map(() => true)
      });
      // Update conversation to include this turn even when items came back, so a follow-up
      // question (if any) can reference what was just extracted.
      if (hasQuestion) {
        setConversation([...nextConversation, { role: 'assistant', content: data.clarifying_question }]);
      } else {
        setConversation(nextConversation);
      }
      setDraft('');
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(detail || t('aiChat.extract.error'));
    } finally {
      setExtracting(false);
    }
  };

  const updateItem = (kind, index, patch) => {
    setProposal((prev) => {
      if (!prev) return prev;
      const next = { ...prev, [kind]: prev[kind].map((it, i) => (i === index ? { ...it, ...patch } : it)) };
      return next;
    });
  };

  const toggleSelected = (kind, index) => {
    setSelected((prev) => ({ ...prev, [kind]: prev[kind].map((v, i) => (i === index ? !v : v)) }));
  };

  const handleCommit = async () => {
    if (!proposal) return;
    // Filter to selected items, remapping cross-refs (new:<idx>) to the new index space.
    const beingIndexMap = new Map();
    const wantIndexMap = new Map();
    const beings = [];
    proposal.beings.forEach((b, i) => {
      if (selected.beings[i]) {
        beingIndexMap.set(i, beings.length);
        beings.push({ title: b.title, description: b.description, type: b.type });
      }
    });
    const remapRef = (ref, kind) => {
      if (!ref) return null;
      if (ref.startsWith('new:')) {
        const oldIdx = parseInt(ref.split(':')[1], 10);
        const map = kind === 'being' ? beingIndexMap : wantIndexMap;
        const newIdx = map.get(oldIdx);
        return newIdx === undefined ? null : `new:${newIdx}`;
      }
      return ref; // existing:<id> passes through
    };
    const wants = [];
    proposal.wants.forEach((w, i) => {
      if (selected.wants[i]) {
        wantIndexMap.set(i, wants.length);
        wants.push({
          title: w.title,
          description: w.description,
          being_ref: remapRef(w.being_ref, 'being'),
          target_date: w.target_date || null
        });
      }
    });
    const todos = proposal.todos
      .map((td, i) => (selected.todos[i] ? {
        title: td.title,
        description: td.description || null,
        want_ref: remapRef(td.want_ref, 'want'),
        being_ref: remapRef(td.being_ref, 'being'),
        due_date: td.due_date || null
      } : null))
      .filter(Boolean);
    const reflections = proposal.reflections
      .map((r, i) => (selected.reflections[i] ? { prompt: r.prompt, response: r.response } : null))
      .filter(Boolean);

    if (beings.length + wants.length + todos.length + reflections.length === 0) {
      toast.info(t('aiChat.review.nothingSelected'));
      return;
    }

    setCommitting(true);
    try {
      const { data } = await axios.post(
        `${API}/ai/commit`,
        { beings, wants, todos, reflections, summary: proposal.summary },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const parts = [];
      if (data.created.beings) parts.push(`${data.created.beings} ${t('layer.being')}`);
      if (data.created.wants) parts.push(`${data.created.wants} ${t('layer.want')}`);
      if (data.created.todos) parts.push(`${data.created.todos} ${t('layer.todo')}`);
      if (data.created.reflections) parts.push(`${data.created.reflections} ${t('aiChat.reflections')}`);
      toast.success(t('aiChat.commit.success', { items: parts.join(', ') }));
      setProposal(null);
      // If the model left a follow-up question, keep the thread alive so the user can answer.
      // Otherwise reset.
      if (proposal.clarifying_question) {
        // The clarifying_question is already in `conversation`, no need to re-append.
      } else {
        setConversation([]);
      }
      setDraft('');
      onCommitted?.();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(detail || t('aiChat.commit.error'));
    } finally {
      setCommitting(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      handleExtract();
    }
  };

  const placeholderText = inThread
    ? t('aiChat.continue.placeholder')
    : t(PLACEHOLDER_KEYS[placeholderIdx]);

  const closePanel = () => {
    if (listening) stopListening();
    setPanelOpen(false);
  };

  return (
    <>
      {/* Floating action button — visible when the panel is closed. */}
      {!panelOpen && (
        <button
          type="button"
          onClick={() => setPanelOpen(true)}
          className="fixed bottom-5 right-5 sm:bottom-6 sm:right-6 z-40 w-14 h-14 rounded-full bg-accent text-accent-foreground shadow-lg hover:shadow-xl hover:scale-105 active:scale-95 transition-all flex items-center justify-center group"
          title={t('aiChat.fab.label')}
          aria-label={t('aiChat.fab.label')}
          data-testid="ai-chat-fab"
        >
          <Sparkles className="w-6 h-6" />
          {/* Subtle dot indicator when there's an active thread the user might want to resume */}
          {inThread && (
            <span className="absolute top-1 right-1 w-2.5 h-2.5 rounded-full bg-background border-2 border-accent" aria-hidden />
          )}
        </button>
      )}

      {/* Panel — visible when open. Mobile: full-width docked to bottom.
          Desktop: 400px wide corner widget. */}
      {panelOpen && (
        <div
          className="ai-bar fixed bottom-0 left-0 right-0 sm:left-auto sm:right-6 sm:bottom-6 sm:w-[28rem] sm:max-w-[calc(100vw-3rem)] z-40 rounded-none sm:rounded-2xl shadow-2xl border-t border-border sm:border"
          data-testid="ai-chat-bar"
        >
          <div className="px-4 sm:px-5 py-3 sm:py-4">
            {/* Panel header with title + close */}
            <div className="flex items-center justify-between mb-2 sm:mb-3">
              <div className="flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-accent" />
                <span className="text-xs uppercase tracking-[0.18em] text-muted-foreground font-medium">{t('aiChat.fab.label')}</span>
              </div>
              <button
                type="button"
                onClick={closePanel}
                className="text-muted-foreground hover:text-foreground transition-colors p-1 rounded-md hover:bg-secondary/60"
                title={t('aiChat.panel.close')}
                aria-label={t('aiChat.panel.close')}
                data-testid="ai-chat-close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Pending clarifying question, shown above the input when the AI has asked one */}
            {pendingQuestion && (
              <div
                className="mb-3 p-3 rounded-xl bg-accent/8 border border-accent/20 flex items-start gap-2 fade-in"
                data-testid="ai-clarifying-question"
              >
                <MessageCircle className="w-4 h-4 text-accent mt-0.5 flex-shrink-0" />
                <p className="text-sm leading-relaxed flex-1 font-serif italic text-foreground/90">
                  {pendingQuestion}
                </p>
                <button
                  type="button"
                  onClick={resetThread}
                  className="text-muted-foreground hover:text-foreground transition-colors text-xs flex items-center gap-1 flex-shrink-0"
                  title={t('aiChat.thread.reset')}
                  data-testid="ai-thread-reset"
                >
                  <RotateCcw className="w-3 h-3" />
                  <span className="hidden sm:inline">{t('aiChat.thread.reset')}</span>
                </button>
              </div>
            )}

            {user?.subscription?.tier === 'starter' ? (
              <div className="bg-[#fcfaf2] dark:bg-[#1c1917] border border-amber-200/50 dark:border-amber-900/50 rounded-2xl p-5 text-center space-y-4">
                <Sparkles className="w-8 h-8 text-amber-500 mx-auto animate-pulse" />
                <h3 className="text-sm font-semibold text-foreground">AI Coach is Locked</h3>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  Interactive AI alignment, habit reflection coaching, and automated weekly reviews are available on Align Pro and Elite Pioneer.
                </p>
                <Button
                  onClick={() => {
                    closePanel();
                    navigate('/checkout');
                  }}
                  className="w-full bg-amber-600 hover:bg-amber-500 text-white rounded-xl text-xs py-2"
                >
                  Upgrade Now
                </Button>
              </div>
            ) : (
              <>
                <div className="flex items-end gap-2">
                  <Textarea
                    ref={textareaRef}
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={onKeyDown}
                    placeholder={placeholderText}
                    rows={2}
                    className="resize-none text-sm sm:text-base min-h-[60px] max-h-32 transition-all border-border/60 focus-visible:ring-accent/40"
                    data-testid="ai-chat-input"
                    disabled={extracting}
                  />
                  <div className="flex flex-col gap-1.5 flex-shrink-0">
                    <Button
                      type="button"
                      size="icon"
                      variant={listening ? 'default' : 'outline'}
                      onClick={listening ? stopListening : startListening}
                      disabled={extracting || !voiceSupported}
                      title={voiceSupported ? t('aiChat.voice.tooltip') : t('aiChat.voice.unsupported')}
                      className={`rounded-full h-9 w-9 ${listening ? 'mic-listening' : ''}`}
                      data-testid="ai-chat-mic"
                    >
                      {listening ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
                    </Button>
                    <Button
                      type="button"
                      size="icon"
                      onClick={handleExtract}
                      disabled={extracting || !draft.trim()}
                      className="rounded-full h-9 w-9"
                      data-testid="ai-chat-send"
                    >
                      {extracting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    </Button>
                  </div>
                </div>
                <p className="text-[10px] sm:text-xs text-muted-foreground mt-1.5 transition-opacity">
                  {listening ? t('aiChat.listening') : inThread ? t('aiChat.continue.hint') : t('aiChat.hint')}
                </p>
              </>
            )}
          </div>
        </div>
      )}

      <Dialog open={!!proposal} onOpenChange={(open) => !open && setProposal(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="ai-review-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">{t('aiChat.review.title')}</DialogTitle>
            {proposal?.summary && (
              <p className="text-sm text-muted-foreground">{proposal.summary}</p>
            )}
          </DialogHeader>

          {proposal && (
            <div className="space-y-6 mt-2">
              {proposal.beings.length > 0 && (
                <ReviewSection
                  title={t('layer.being')}
                  items={proposal.beings}
                  selected={selected.beings}
                  onToggle={(i) => toggleSelected('beings', i)}
                  renderItem={(b, i) => (
                    <>
                      <Input
                        value={b.title}
                        onChange={(e) => updateItem('beings', i, { title: e.target.value })}
                        className="font-serif text-sm"
                      />
                      <Textarea
                        value={b.description}
                        onChange={(e) => updateItem('beings', i, { description: e.target.value })}
                        rows={2}
                        className="resize-none text-xs sm:text-sm font-serif"
                      />
                      <select
                        value={b.type}
                        onChange={(e) => updateItem('beings', i, { type: e.target.value })}
                        className="w-full p-2 rounded-lg bg-background border border-border text-xs"
                      >
                        {BEING_TYPES.map((tp) => (
                          <option key={tp} value={tp}>{t(`being.type.${tp}`)}</option>
                        ))}
                      </select>
                    </>
                  )}
                />
              )}

              {proposal.wants.length > 0 && (
                <ReviewSection
                  title={t('layer.want')}
                  items={proposal.wants}
                  selected={selected.wants}
                  onToggle={(i) => toggleSelected('wants', i)}
                  renderItem={(w, i) => (
                    <>
                      <Input
                        value={w.title}
                        onChange={(e) => updateItem('wants', i, { title: e.target.value })}
                        className="text-sm"
                      />
                      <Textarea
                        value={w.description}
                        onChange={(e) => updateItem('wants', i, { description: e.target.value })}
                        rows={2}
                        className="resize-none text-xs sm:text-sm"
                      />
                    </>
                  )}
                />
              )}

              {proposal.todos.length > 0 && (
                <ReviewSection
                  title={t('layer.todo')}
                  items={proposal.todos}
                  selected={selected.todos}
                  onToggle={(i) => toggleSelected('todos', i)}
                  renderItem={(td, i) => (
                    <>
                      <Input
                        value={td.title}
                        onChange={(e) => updateItem('todos', i, { title: e.target.value })}
                        className="text-sm"
                      />
                      {td.description !== null && td.description !== undefined && (
                        <Textarea
                          value={td.description || ''}
                          onChange={(e) => updateItem('todos', i, { description: e.target.value })}
                          rows={2}
                          className="resize-none text-xs sm:text-sm"
                        />
                      )}
                    </>
                  )}
                />
              )}

              {proposal.reflections.length > 0 && (
                <ReviewSection
                  title={t('aiChat.reflections')}
                  items={proposal.reflections}
                  selected={selected.reflections}
                  onToggle={(i) => toggleSelected('reflections', i)}
                  renderItem={(r, i) => (
                    <>
                      <Input
                        value={r.prompt}
                        onChange={(e) => updateItem('reflections', i, { prompt: e.target.value })}
                        className="text-sm font-medium"
                      />
                      <Textarea
                        value={r.response}
                        onChange={(e) => updateItem('reflections', i, { response: e.target.value })}
                        rows={3}
                        className="resize-none text-xs sm:text-sm"
                      />
                    </>
                  )}
                />
              )}

              {proposal.clarifying_question && (
                <div className="p-3 rounded-lg bg-accent/8 border border-accent/20 flex items-start gap-2">
                  <MessageCircle className="w-4 h-4 text-accent mt-0.5 flex-shrink-0" />
                  <div className="text-sm">
                    <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">
                      {t('aiChat.review.followup')}
                    </p>
                    <p className="font-serif italic text-foreground/90">{proposal.clarifying_question}</p>
                  </div>
                </div>
              )}
            </div>
          )}

          <DialogFooter className="gap-2 mt-4">
            <Button
              variant="ghost"
              onClick={() => setProposal(null)}
              disabled={committing}
              data-testid="ai-review-cancel"
            >
              <X className="w-4 h-4 mr-2" />
              {t('form.cancel')}
            </Button>
            <Button
              onClick={handleCommit}
              disabled={committing}
              data-testid="ai-review-commit"
            >
              {committing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
              {t('aiChat.review.commit')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function ReviewSection({ title, items, selected, onToggle, renderItem }) {
  return (
    <section className="space-y-3">
      <h3 className="font-serif text-base font-light tracking-tight uppercase text-muted-foreground">{title}</h3>
      <div className="space-y-3">
        {items.map((item, i) => (
          <div
            key={i}
            className={`flex gap-3 p-3 rounded-lg border ${selected[i] ? 'border-border bg-card/50' : 'border-border/30 bg-card/10 opacity-60'}`}
          >
            <Checkbox
              checked={selected[i]}
              onCheckedChange={() => onToggle(i)}
              className="mt-1 flex-shrink-0"
              data-testid={`ai-review-toggle-${title}-${i}`}
            />
            <div className="flex-1 space-y-2 min-w-0">
              {renderItem(item, i)}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
